import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { createPartnerCategory, fetchPartnerTypes, fetchBusinessUnitsByDomain } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function PartnerCategoryCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [partnerTypes, setPartnerTypes] = useState([]);
  const [businessUnitId, setBusinessUnitId] = useState(null);
  const [form, setForm] = useState({
    partnerCategoryName: "",
    partnerCategoryDescription: "",
    selectedPartnerTypeId: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [types, units] = await Promise.all([
          fetchPartnerTypes(businessDomainId),
          fetchBusinessUnitsByDomain(businessDomainId),
        ]);
        setPartnerTypes(Array.isArray(types) ? types : []);
        if (Array.isArray(units) && units.length > 0) {
          setBusinessUnitId(units[0].businessUnitId);
        }
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createPartnerCategory({
        companyId: COMPANY_ID,
        businessDomainId,
        businessUnitId,
        partnerCategoryName: form.partnerCategoryName,
        partnerCategoryDescription: form.partnerCategoryDescription || null,
        partnerTypeId: form.selectedPartnerTypeId || null,
        associatedPartnerTypes: [],
      });
      notify.success(t("message.create.success"));
      navigate(`/strategy/${businessDomainId}/partner-categories`, { replace: true });
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("partnerCategory.create.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("partnerCategory.partnerType")}</InputLabel>
              <Select
                value={form.selectedPartnerTypeId}
                onChange={(e) => setForm({ ...form, selectedPartnerTypeId: e.target.value })}
                label={t("partnerCategory.partnerType")}
              >
                {partnerTypes.map((pt) => (
                  <MenuItem key={pt.partnerTypeId} value={pt.partnerTypeId}>
                    {pt.partnerTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("partnerCategory.name")}
              name="partnerCategoryName"
              value={form.partnerCategoryName}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("common.description")}
              name="partnerCategoryDescription"
              value={form.partnerCategoryDescription}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/partner-categories`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
